import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { DataTableBuilderComponent } from '@/components/data-table-builder/data-table-builder.component';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import {
  GroupedQuestionsResponseDTO,
  DacQuestionsGroupDTO,
} from '@/types/clarification-request.types';
import { ClarificationService } from '../clarification-request.service';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { getClarificationDataTableStructure } from './utils/clarification-request-data-table-object';
import { AuthorizationService } from '@/services/authorization.service';

// Type for Redacteur DAC groups
export interface RedacteurDacGroup {
  dacReference: string;
  questionCount: number;
}

@Component({
  selector: 'sm-clarification-current-questions',
  standalone: true,
  templateUrl: './current-questions.component.html',
  styleUrl: './current-questions.component.scss',
  imports: [CommonModule, PageHeaderComponent, DataTableBuilderComponent, TranslateModule],
})
export class ClarificationCurrentQuestionsComponent implements OnInit {
  clarificationService = inject(ClarificationService);
  messageService = inject(MessageService);
  router = inject(Router);
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);

  breadcrumb = [
    {
      label: 'clarification.title',
      routerLink: ['/clarification'],
    },
  ];

  // Redacteur actions
  redacteurRowActionsFn: (target: RedacteurDacGroup) => DataTableRowActions = (target: RedacteurDacGroup) => {
    return {
      defaultActions: {
        edit: {
          label: 'clarification.actions.treat',
        },
      },
      editAction: () => {
        // Navigate to the new redacteur-dac-group component using query params to handle slashes
        this.router.navigate(['/clarification-question'], { queryParams: { dacReference: target.dacReference } });
      },
    };
  };

  // Redacteur data
  redacteurData: RedacteurDacGroup[] = [];
  redacteurDataTableObject?: DynamicDataTable;
  isRedacteur = this.authService.isRedacteur();

  loading$ = new BehaviorSubject<boolean>(true);
  loadingTasks$ = new BehaviorSubject<boolean>(true);

  ngOnInit(): void {
    // Clear cache to ensure fresh data when navigating to this page
    this.clarificationService.clearQuestionsTasksCache();
    this.clarificationService.clearUserTasksCache();

    // Initialize redacteur table
    if (this.isRedacteur) {
      this.redacteurDataTableObject = getClarificationDataTableStructure({
        rowActionsFn: (target: RedacteurDacGroup) => this.redacteurRowActionsFn(target),
        isValidator: false,
        isRedacteur: true,
      });
      this.loadData();
    } else {
      this.loading$.next(false);
      this.loadingTasks$.next(false);
    }
  }

  loadData() {
    this.loading$.next(true);
    this.loadingTasks$.next(true);

    // REDACTEUR_AC: Use GET /api/questions/redacteur API
    this.clarificationService.getQuestionsForRedacteur().subscribe({
      next: (response: GroupedQuestionsResponseDTO) => {
        this.redacteurData = (response.groups || []).map((group: DacQuestionsGroupDTO) => ({
          dacReference: group.dacReference,
          questionCount: group.questions?.length || 0,
        }));
        this.loading$.next(false);
        this.loadingTasks$.next(false);
      },
      error: (error: any) => {
        this.loading$.next(false);
        this.loadingTasks$.next(false);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('clarification.messages.error_summary'),
          detail: error.error?.message || this.translate.instant('clarification.messages.error_loading_clarification_requests'),
        });
      },
    });
  }
}
